import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
  useTheme,
  alpha,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface HotelCategoryData {
  category: string;
  adr: number;
  occupancy: number;
  revpar: number;
  adr_change?: number;
  occupancy_change?: number;
  revpar_change?: number;
}

interface HotelCategoryChartProps {
  data: HotelCategoryData[];
  loading?: boolean;
  title?: string;
}

type MetricType = 'adr' | 'occupancy' | 'revpar';

export const HotelCategoryChart: React.FC<HotelCategoryChartProps> = ({
  data,
  loading = false,
  title = 'Performance by Hotel Category',
}) => {
  const theme = useTheme();
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('revpar');

  // Hotel class color scheme
  const hotelClassColors: Record<string, string> = {
    'All': '#a6a6a6',           // RGB(166,166,166)
    'Luxury': '#003f9f',         // RGB(0,63,159)
    'Upper Upscale': '#16d9bc',  // RGB(22,217,188)
    'Upscale': '#0c7876',        // RGB(12,120,118)
    'Upper Midscale': '#32a8fd', // RGB(50,168,253)
    'Midscale': '#ea80ff',       // RGB(234,128,255)
    'Economy': '#7b2a5c',        // RGB(123,42,92)
    'Unclassified': '#808080'    // RGB(128,128,128)
  };

  const handleMetricChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMetric: MetricType | null
  ) => {
    if (newMetric !== null) {
      setSelectedMetric(newMetric);
    }
  };

  // Filter out 'All' category if present
  const chartData = data.filter(item => item.category !== 'All');

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 2,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {label}
          </Typography>
          <Typography variant="body2" color="primary">
            {selectedMetric.toUpperCase()}: {
              selectedMetric === 'occupancy'
                ? `${payload[0].value.toFixed(1)}%`
                : `$${payload[0].value.toFixed(2)}`
            }
          </Typography>
          {data[`${selectedMetric}_change`] !== undefined && (
            <Typography
              variant="caption"
              sx={{
                color: data[`${selectedMetric}_change`] >= 0 ? 'success.main' : 'error.main',
              }}
            >
              YoY: {data[`${selectedMetric}_change`] >= 0 ? '+' : ''}
              {data[`${selectedMetric}_change`].toFixed(1)}%
            </Typography>
          )}
        </Box>
      );
    }
    return null;
  };

  // Custom label for bars
  const CustomLabel = (props: any) => {
    const { x, y, width, value } = props;
    const data = chartData.find(item => item[selectedMetric] === value);
    const change = data?.[`${selectedMetric}_change`];
    
    if (change === undefined) return null;
    
    return (
      <text
        x={x + width / 2}
        y={y - 5}
        fill={change >= 0 ? theme.palette.success.main : theme.palette.error.main}
        textAnchor="middle"
        fontSize={10}
        fontWeight={600}
      >
        {change >= 0 ? '+' : ''}{change.toFixed(1)}%
      </text>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width="40%" height={32} />
          <Skeleton variant="rectangular" width="100%" height={350} sx={{ mt: 3 }} />
        </CardContent>
      </Card>
    );
  }

  const getYAxisLabel = () => {
    switch (selectedMetric) {
      case 'adr':
        return 'Average Daily Rate ($)';
      case 'occupancy':
        return 'Occupancy Rate (%)';
      case 'revpar':
        return 'Revenue per Available Room ($)';
      default:
        return '';
    }
  };

  // Custom bar shape to apply category-specific colors
  const CustomBar = (props: any) => {
    const { fill, x, y, width, height, payload } = props;
    const categoryColor = hotelClassColors[payload.category] || theme.palette.primary.main;

    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={categoryColor}
        rx={4}
        ry={4}
      />
    );
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          
          <ToggleButtonGroup
            value={selectedMetric}
            exclusive
            onChange={handleMetricChange}
            size="small"
          >
            <ToggleButton value="adr">ADR</ToggleButton>
            <ToggleButton value="occupancy">Occupancy</ToggleButton>
            <ToggleButton value="revpar">RevPAR</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ width: '100%', height: 350 }}>
          <ResponsiveContainer>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
              <XAxis
                dataKey="category"
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
                stroke={theme.palette.text.secondary}
              />
              <YAxis
                tick={{ fontSize: 12 }}
                stroke={theme.palette.text.secondary}
                label={{
                  value: getYAxisLabel(),
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 12 },
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              
              <Bar
                dataKey={selectedMetric}
                shape={<CustomBar />}
                label={<CustomLabel />}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          * Percentages indicate year-over-year change
        </Typography>
      </CardContent>
    </Card>
  );
};